// Dunia mini game Sopir — jalur hutan berkelok (zig-zag) dalam koordinat grid isometrik.
// Sumbu grid x → layar kanan-atas, sumbu y → layar kiri-atas. Bus melaju ke arah sudut `th` (0 = +x, π/2 = +y);
// tahan layar → sasaran 0 (drift kanan), lepas → sasaran π/2 (drift kiri). Arah laju mengejar sasaran dengan lambat
// (meluncur), badan bus mengejar lebih cepat → sudut selip ala drift; roda belakang meninggalkan bekas ban.
// "Kedalaman" D = x + y dipakai sebagai jarak tempuh → kesulitan (laju naik, jalur menyempit, lurusan memendek).
export const DURASI = 90, TARGET = 36, TOLERANSI = 0.12;    // detik · poin untuk skor 100 · kelonggaran badan bus (tile)
export const BUS = { L: 1.5, W: 0.62, H: 0.74 }, TEBAL = 1.2, LANTAI = 6.5;   // ukuran bus · tebal jalan layang · kedalaman lantai hutan
const K_LAJU = 3.4, K_PEGAS = 62, K_REDAM = 10;                   // laju putar arah laju · pegas-redam putaran badan (sedikit underdamped → ekor goyang)
const SASARAN = { x: 0, y: Math.PI / 2, d: Math.PI / 4 };          // sudut arah: kanan · kiri · lurus (awal)                           // laju putar arah laju & badan (per detik)

export const lebar = D => Math.max(4, 6 - D * 0.008);
export const laju = (D, lv) => Math.min(7.5, 4 + lv * 0.1 + D * 0.025) * Math.min(1, 0.45 + D / 22);
const rentangLurus = D => { const hi = Math.max(7, 11 - Math.floor(D / 50)); return [5, hi]; };

export const JENIS = {
  anak_sekolah: { tarif: 2, nama: ["Dik Rizal", "Dik Nia", "Dik Fajar", "Dik Putri"], baju: "#f4f4f4", celana: "#c62828", topi: "#c62828" },
  pekerja: { tarif: 3, nama: ["Pak Karyo", "Mas Dimas", "Mbak Sari", "Pak Yono"], baju: "#2e58c4", celana: "#2b2b2b", helm: "#f2c200" },
  mahasiswa: { tarif: 3, nama: ["Mas Angga", "Mbak Dewi", "Mas Reza"], baju: "#2e9e5b", celana: "#3b3b60", ransel: "#141414" },
  ibu_pasar: { tarif: 4, nama: ["Bu Tini", "Bu Marni", "Mbok Yem", "Bu Lastri"], baju: "#9a4f5f", celana: "#5a3a4a", jilbab: "#e0a030", keranjang: "#b8864b" },
  mbah: { tarif: 5, nama: ["Mbah Kakung", "Mbah Putri", "Mbah Darmo"], baju: "#8a5a3c", celana: "#4a4a4a", blangkon: "#3a2a1a", tongkat: "#6e4523" },
};
// Rintangan di aspal: denda poin bila ditabrak; batu & kayu terpental, lubang tetap. Muncul sejak D > 12, makin rapat seiring jarak
export const RINTANGAN = { batu: { denda: 2, label: "Nabrak batu" }, kayu: { denda: 3, label: "Nabrak kayu" }, lubang: { denda: 1, label: "Kena lubang" } };
const UNDI = [["anak_sekolah", 0.3], ["pekerja", 0.3], ["ibu_pasar", 0.2], ["mahasiswa", 0.12], ["mbah", 0.08]];

export function buatDunia(seed, lv) {
  let n = 0;
  const rnd = () => { const x = Math.sin(seed + (n++) * 97.3) * 10000; return x - Math.floor(x); };
  // Lurusan awal diagonal (x = y → lurus ke atas layar), bus langsung melaju; tikungan pertama pasti ke kanan (+x)
  const awal = { x: 0, y: 0 }, ujung = { x: 14, y: 14 };
  const s = { rnd, lv, titik: [awal, ujung], arahBerikut: "x", segmen: [{ a: awal, b: ujung, arah: "d", w: lebar(0) }], penumpang: [], idP: 0, rintangan: [], idR: 0, pohon: new Map(), pohonBaru: [],
    pos: { x: 0, y: 0 }, arah: "d", th: Math.PI / 4, thB: Math.PI / 4, wB: 0, roll: 0, jalan: true, jatuh: null, jejak: [], jejakN: 0, asap: [], cam: { x: 0, y: 0 }, poin: 0, naik: 0, selesai: false, tonggak: 0 };
  pastikanJalur(s, 60);
  return s;
}

function undiJenis(rnd) { let u = rnd(); for (const [k, p] of UNDI) { if (u < p) return k; u -= p; } return "pekerja"; }

// Perpanjang jalur hingga kedalaman `sampai`; tiap lurusan baru berpeluang menaruh satu penumpang di tepi jalur
export function pastikanJalur(s, sampai) {
  while (true) {
    const akhir = s.titik[s.titik.length - 1];
    if (akhir.x + akhir.y >= sampai) return;
    const D = akhir.x + akhir.y, [lo, hi] = rentangLurus(D), len = lo + Math.floor(s.rnd() * (hi - lo + 1)), ar = s.arahBerikut;
    const baru = ar === "x" ? { x: akhir.x + len, y: akhir.y } : { x: akhir.x, y: akhir.y + len };
    const w = lebar(D);
    s.segmen.push({ a: akhir, b: baru, arah: ar, w });
    s.titik.push(baru); s.arahBerikut = ar === "x" ? "y" : "x";
    const diSegmen = (t, sisi) => (ar === "x" ? { x: akhir.x + t, y: akhir.y + sisi } : { x: akhir.x + sisi, y: akhir.y + t });
    let tP = -9;
    if (s.rnd() < 0.7 && D > 2) {
      // penumpang berdiri di sembarang posisi melintang jalur → sopir harus mengarahkan bus untuk "menabrak"nya
      tP = 1.5 + s.rnd() * (len - 2); const sisi = (s.rnd() * 2 - 1) * (w / 2 - 0.6), jenis = undiJenis(s.rnd), j = JENIS[jenis];
      s.penumpang.push({ id: s.idP++, jenis, tarif: j.tarif, nama: j.nama[Math.floor(s.rnd() * j.nama.length)], naik: false, lewat: false, ...diSegmen(tP, sisi), sisi });
    }
    // rintangan: 0–2 per lurusan, peluang naik seiring jarak; jauh dari penumpang & ujung lurusan
    const nR = D < 12 ? 0 : (s.rnd() < Math.min(0.85, 0.4 + D / 160) ? 1 : 0) + (D > 70 && s.rnd() < 0.5 ? 1 : 0);
    for (let i = 0; i < nR; i++) {
      let t = 1.5 + s.rnd() * (len - 2); if (Math.abs(t - tP) < 1.6) t = tP + (t < tP ? -1.6 : 1.6);
      if (t < 1.2 || t > len - 1.2) continue;
      const u = s.rnd(), jenis = u < 0.45 ? "batu" : u < 0.75 ? "kayu" : "lubang";
      s.rintangan.push({ id: s.idR++, jenis, denda: RINTANGAN[jenis].denda, kena: false, ...diSegmen(t, (s.rnd() * 2 - 1) * (w / 2 - 0.7)), putar: s.rnd() * Math.PI });
    }
  }
}

const jarakSegmen = (p, a, b) => {
  const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2));
  return Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t));
};
export const diJalur = (s, p) => s.segmen.some(g => jarakSegmen(p, g.a, g.b) <= g.w / 2 + TOLERANSI);
export const jarakJalur = (s, p) => Math.min(...s.segmen.map(g => jarakSegmen(p, g.a, g.b)));

const hash = (i, j) => { const x = Math.sin(i * 127.1 + j * 311.7) * 43758.5453; return x - Math.floor(x); };
// Pohon hutan per sel grid (dibuat sekali saat sel pertama terlihat; sel jauh di belakang dibuang)
export function pastikanPohon(s, cx, cy, R) {
  const D0 = s.pos.x + s.pos.y;
  for (let i = Math.floor(cx - R); i <= cx + R; i++) for (let j = Math.floor(cy - R); j <= cy + R; j++) {
    const k = `${i},${j}`;
    if (s.pohon.has(k) || i + j < D0 - 20) continue;
    const h = hash(i, j), h2 = hash(j + 7, i - 3), x = i + 0.15 + h2 * 0.7, y = j + 0.15 + hash(i + 11, j + 5) * 0.7;
    const ada = h < 0.42 && jarakJalur(s, { x, y }) > 3.9;
    const p = ada ? { x, y, ukuran: 0.8 + h2 * 0.6, jenis: h > 0.3 ? "pinus" : "rimbun", nada: Math.floor(h2 * 3) } : null;
    s.pohon.set(k, p); if (p) s.pohonBaru.push(p);
  }
  if (s.pohon.size > 5000) for (const k of [...s.pohon.keys()]) { const [i, j] = k.split(",").map(Number); if (i + j < D0 - 20) s.pohon.delete(k); }
}

// Posisi roda belakang kiri/kanan (grid) dari pose badan bus
export function rodaBelakang(s) {
  const c = Math.cos(s.thB), sn = Math.sin(s.thB), u = -BUS.L / 2 + 0.28, v = BUS.W / 2 - 0.04;
  return [{ x: s.pos.x + c * u + sn * v, y: s.pos.y + sn * u - c * v }, { x: s.pos.x + c * u - sn * v, y: s.pos.y + sn * u + c * v }];
}
export const sedangDrift = s => Math.abs(s.thB - s.th) > 0.06 || Math.abs(s.wB) > 0.9;
// Titik p berada di dalam kotak badan bus (koordinat lokal u sepanjang badan, v melintang) + sedikit kelonggaran
export function ditabrak(s, p, ekstra = 0) {
  const c = Math.cos(s.thB), sn = Math.sin(s.thB), dx = p.x - s.pos.x, dy = p.y - s.pos.y;
  const u = dx * c + dy * sn, v = -dx * sn + dy * c;
  return Math.abs(u) < BUS.L / 2 + 0.15 + ekstra && Math.abs(v) < BUS.W / 2 + 0.22 + ekstra;
}

// Satu langkah simulasi; mengembalikan daftar kejadian: naik | lewat | jatuh | tonggak
export function langkah(s, dt, t) {
  const ev = [];
  if (!s.jalan || s.selesai) return ev;
  const D = s.pos.x + s.pos.y, v = laju(D, s.lv), sasaran = SASARAN[s.arah];
  if (s.jatuh) {
    s.jatuh.t += dt;
    s.pos.x += Math.cos(s.th) * v * 0.35 * dt; s.pos.y += Math.sin(s.th) * v * 0.35 * dt;
    return ev;
  }
  // arah laju meluncur pelan ke sasaran; badan menyusul lebih cepat (oversteer) → sudut selip
  s.th += (sasaran - s.th) * (1 - Math.exp(-K_LAJU * dt));
  s.wB += ((sasaran - s.thB) * K_PEGAS - s.wB * K_REDAM) * dt; s.thB += s.wB * dt;
  s.pos.x += Math.cos(s.th) * v * dt; s.pos.y += Math.sin(s.th) * v * dt;
  pastikanJalur(s, D + 70);
  if (!diJalur(s, s.pos)) { s.jatuh = { t: 0 }; ev.push("jatuh"); return ev; }
  const [ki, ka] = rodaBelakang(s), drift = sedangDrift(s);
  s.jejak.push({ ki, ka, t, drift }); s.jejakN++; if (s.jejak.length > 900) s.jejak.shift();
  if (drift && s.rnd() < 0.5) s.asap.push({ x: (ki.x + ka.x) / 2 + (s.rnd() - 0.5) * 0.3, y: (ki.y + ka.y) / 2 + (s.rnd() - 0.5) * 0.3, vx: (s.rnd() - 0.5) * 0.5, vy: (s.rnd() - 0.5) * 0.5, t: 0, r: 0.1 + s.rnd() * 0.12 });
  for (const p of s.penumpang) {
    if (p.naik || p.lewat) continue;
    if (ditabrak(s, p)) { p.naik = true; p.t = t; s.poin += p.tarif; s.naik += 1; ev.push({ naik: p }); }
    else if (p.x + p.y < D - 2.2) { p.lewat = true; s.poin = Math.max(0, s.poin - 1); ev.push({ lewat: p }); }
  }
  for (const r of s.rintangan) {
    if (r.kena || r.x + r.y < D - 3 || r.x + r.y > D + 3) continue;
    if (ditabrak(s, r, r.jenis === "lubang" ? 0.1 : 0.3)) { r.kena = true; r.t = t; s.poin = Math.max(0, s.poin - r.denda); s.wB += (s.rnd() < 0.5 ? -1 : 1) * 4; ev.push({ rintangan: r }); }
  }
  const tg = Math.floor(D / 40);
  if (tg > s.tonggak) { s.tonggak = tg; ev.push("tonggak"); }
  for (const a of s.asap) { a.t += dt; a.x += a.vx * dt; a.y += a.vy * dt; }
  s.asap = s.asap.filter(a => a.t < 0.7);
  // kamera mengikuti posisi bus (bukan arah laju) dengan offset tetap ke arah "lurus" → tidak berayun saat drift
  const target = { x: s.pos.x + 1.8, y: s.pos.y + 1.8 }, k = 1 - Math.exp(-6 * dt);
  s.cam.x += (target.x - s.cam.x) * k; s.cam.y += (target.y - s.cam.y) * k;
  return ev;
}

export function belok(s, arah) {
  if (s.arah === arah) return;
  s.arah = arah;
}
